/**
 * /register — create a new email + password account.
 *
 * Accepted search params:
 *   - `email`: pre-fill the email field. When present (typically from an
 *     invitation link), the field is locked so the user signs up with the
 *     exact address they were invited at. Otherwise it's editable.
 *   - `invitation`: id of a pending invitation to show as a friendly banner
 *     ("you've been invited to <household>"). Pre-filling the household
 *     name requires a lookup which is left to the in-app InvitationToast
 *     once the user is signed in.
 */

import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { AuthShell } from "../components/AuthShell";
import { GoogleIcon } from "../components/GoogleIcon";
import { PasswordInput } from "../components/PasswordInput";
import { TermsAcceptance } from "../components/TermsAcceptance";
import { Button, Field, Icon, Input, Stack } from "../components/ui";
import { t } from "../i18n";
import { registerSchema } from "../lib/schemas";
import { getApiBase, trpc } from "../lib/trpc";
import { fieldError } from "../lib/validation";

interface RegisterSearch {
  email?: string | undefined;
  invitation?: string | undefined;
}

export const Route = createFileRoute("/register")({
  validateSearch: (raw: Record<string, unknown>): RegisterSearch => ({
    email: typeof raw.email === "string" ? raw.email : undefined,
    invitation: typeof raw.invitation === "string" ? raw.invitation : undefined,
  }),
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const search = Route.useSearch();

  const prefilledEmail = search.email ?? "";
  const emailLocked = !!search.email;

  // Affirmative ToS + privacy acceptance is required before EITHER the
  // password submit OR the Google sign-up flow runs. Under avtaleloven a
  // click counts as informed consent only if the user could plausibly
  // have read the terms first — which is why the legal pages are mounted
  // as public routes and the checkbox label links to them.
  //
  // UX: both buttons stay clickable; if the user clicks without checking
  // we surface an inline error next to the checkbox rather than silently
  // disabling the buttons. Discoverable > slick-but-mute.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState<string | undefined>(undefined);

  /**
   * Runs the supplied action only if the terms are accepted. Surfaces the
   * inline error next to the checkbox otherwise. Use for any action that
   * requires consent (currently: password submit + Google sign-up).
   */
  const requireAcceptance = (action: () => void) => {
    if (!acceptedTerms) {
      setAcceptanceError(t.register.acceptanceRequired);
      return;
    }
    setAcceptanceError(undefined);
    action();
  };

  const acceptInvitation = trpc.invitation.accept.useMutation();
  const register = trpc.auth.register.useMutation({
    onSuccess: async () => {
      utils.auth.me.invalidate();
      // If the user came from a deep-link invitation, accept it now and
      // route them straight to the household.
      if (search.invitation) {
        try {
          const result = await acceptInvitation.mutateAsync({
            invitationId: search.invitation,
          });
          navigate({
            to: "/households/$householdId/$tab",
            params: { householdId: result.householdId, tab: "settlements" },
          });
          return;
        } catch {
          // Invitation expired or invalid — drop the user on the home
          // page; the InvitationToast in AppShell will surface the issue.
        }
      }
      // No "verify your email" interstitial in v0.1: Resend domain isn't
      // verified yet, so showing "check your inbox" would just confuse.
      // Drop straight onto the app. Re-introduce the interstitial when
      // email verification is actually wired up.
      navigate({ to: "/" });
    },
  });

  const form = useForm({
    defaultValues: { email: prefilledEmail, displayName: "", password: "" },
    validators: { onChange: registerSchema, onSubmit: registerSchema },
    onSubmit: ({ value }) => {
      register.mutate({
        email: value.email.trim(),
        displayName: value.displayName.trim(),
        password: value.password,
      });
    },
  });

  return (
    <AuthShell title={t.register.title} description={t.register.description}>
      <Stack gap={4}>
        {search.invitation && (
          <div className="rounded-md border border-accent-200 bg-accent-50 p-3 text-accent-900 text-sm dark:border-accent-800 dark:bg-accent-900/30 dark:text-accent-100">
            <div className="flex items-start gap-2">
              <Icon name="mail" size="sm" className="mt-0.5 shrink-0" />
              <span>{t.register.invitationBanner(search.invitation)}</span>
            </div>
          </div>
        )}

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            requireAcceptance(() => form.handleSubmit());
          }}
        >
          <form.Field name="email">
            {(field) => (
              <Field
                label={t.register.emailLabel}
                hint={emailLocked ? t.register.emailLockedHint : undefined}
                error={fieldError(field)}
              >
                <Input
                  type="email"
                  autoComplete="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={emailLocked || register.isPending}
                  iconLeft="mail"
                  placeholder={t.register.emailPlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <form.Field name="displayName">
            {(field) => (
              <Field label={t.register.displayNameLabel} error={fieldError(field)}>
                <Input
                  type="text"
                  autoComplete="name"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={register.isPending}
                  placeholder={t.register.displayNamePlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <form.Field name="password">
            {(field) => (
              <Field
                label={t.register.passwordLabel}
                hint={t.register.passwordHint}
                error={fieldError(field)}
              >
                <PasswordInput
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={register.isPending}
                  placeholder={t.register.passwordPlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <TermsAcceptance
            checked={acceptedTerms}
            onChange={(next) => {
              setAcceptedTerms(next);
              if (next) setAcceptanceError(undefined);
            }}
            error={acceptanceError}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={register.isPending}
            disabled={register.isPending}
          >
            {register.isPending ? t.register.submitting : t.register.submitButton}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() =>
              requireAcceptance(() => {
                if (search.invitation) {
                  try {
                    sessionStorage.setItem("fellesen.pendingInvitation", search.invitation);
                  } catch {
                    // sessionStorage unavailable — proceed without stashing.
                  }
                }
                window.location.href = `${getApiBase()}/auth/google/start`;
              })
            }
          >
            <GoogleIcon size="sm" />
            {t.register.googleSignUp}
          </Button>
          {register.isError && (
            <p className="text-rose-600 text-xs dark:text-rose-400" role="alert">
              {register.error.message}
            </p>
          )}
        </form>

        <div className="text-center text-ink-500 text-xs">
          {t.register.alreadyHaveAccount}{" "}
          <Link
            to="/"
            className="font-medium text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline dark:text-accent-300 dark:hover:text-accent-200"
          >
            {t.register.loginLink}
          </Link>
        </div>
      </Stack>
    </AuthShell>
  );
}
